// ecs/effects.ts
// Side-effect executor. Converts queued effects into concrete UI/audio/network calls.

import { playImpactSound, playPickupSound } from "../audio/audio";
import { processBulletUpdate } from "../game/bullets.interpolate";
import { requestLobbyList, updateLobbyList } from "../lobby/browser";
import { dispatchNetworkMessage } from "../networking/network.dispatch";
import { gameStateStore } from "@game/state";
import { updateCountdown as updateCountdownUI } from "../ui/countdown.ui";
import { useLeaderboardStore } from "../ui/leaderboard.store";
import { hideJoinModal, showJoinModal } from "../ui/modals";
import { showToast } from "../ui/toasts";
import { getTheme } from "../visuals/theme";
import { uiBridge } from "../ui/ui.bridge";
import type { Effect, World } from "./core";

// Table-driven registry for effect handlers for maintainability.
const effectHandlers: {
  [K in Effect["kind"]]?: (
    e: Extract<Effect, { kind: K }>,
    world: World,
  ) => void;
} = {
  toast: (e) => showToast(e.message, e.level, e.ms),
  sound: (e) =>
    e.sound === "impact" ? playImpactSound() : playPickupSound(e.amount ?? 1),
  "storage.set": (e) => localStorage.setItem(e.key, e.value),
  "storage.remove": (e) => localStorage.removeItem(e.key),
  "ws.send": (e) => dispatchNetworkMessage(e.payload),
  "bullets.recompute": (_e, world) =>
    processBulletUpdate(world.deps.state.bullets),
  "ui.topbar": (e, world) => {
    if (world.deps.topbarEl)
      world.deps.topbarEl.classList.toggle("show", e.show);
  },
  "ui.joinModal": (e) => (e.show ? showJoinModal() : hideJoinModal()),
  "ui.buttons": (e, world) => world.deps.updateButtons(e.inLobby),
  "ui.resizeCanvas": (_e, world) => world.deps.resizeCanvas(),
  "ui.render": (_e, world) => world.deps.render(),
  "ui.leaderboard.visible": (e) =>
    useLeaderboardStore.getState().setVisible(e.show),
  "ui.countdown": (e) =>
    updateCountdownUI({
      secondsUntilStart: e.seconds ?? null,
      isActive: e.active,
      text: e.text,
    }),
  "ui.playersPills": (e) => {
    const el = document.getElementById("players");
    if (el) {
      el.innerHTML = "";
      e.names.forEach((p) => {
        const span = document.createElement("span");
        span.className = "pill";
        span.textContent = p.name + (p.self ? " (you)" : "");
        el.appendChild(span);
      });
    }
  },
  "ui.gameStats": (e, world) => {
    if (world.deps.gameStatsEl)
      world.deps.gameStatsEl.style.display = e.show ? "flex" : "none";
  },
  "ui.perfStats": (e, world) => {
    if (world.deps.perfStatsEl)
      world.deps.perfStatsEl.style.display = e.show ? "flex" : "none";
  },
  "ui.deathModal": (e) => {
    const title = document.getElementById("modalTitle");
    const msg = document.getElementById("modalMessage");
    const modal = document.getElementById("deathModal");
    if (title) {
      title.textContent = e.title ?? "";
      title.className = e.titleClass ?? "";
    }
    if (msg && e.message) msg.textContent = e.message;
    if (modal) modal.classList.toggle("show", e.show);
  },
  "ui.deathStats": (e) => {
    const set = (id: string, v: any) => {
      const el = document.getElementById(id);
      if (el && v != null) el.textContent = String(v);
    };
    set("statKills", e.stats.kills);
    set("statDeaths", e.stats.deaths);
    set("statPellets", e.stats.pelletsCollected);
    set("statMaxHealth", e.stats.maxHealth);
    if (e.stats.damageMultiplier != null)
      set("statDamage", `${e.stats.damageMultiplier.toFixed(2)}x`);
    if (e.stats.speedMultiplier != null)
      set("statSpeed", `${e.stats.speedMultiplier.toFixed(2)}x`);
  },
  "ui.setGameModeState": (e, world) => {
    // Update Zustand store for React components (like minimap)
    // Convert flags array to Map for Zustand store
    const flagsMap = new Map<string, any>();
    if (e.gameModeState.flags) {
      for (const flag of e.gameModeState.flags) {
        flagsMap.set(flag.id, flag);
      }
    }

    // Update both gameModeState and flags Map
    world.deps.setGlobalState({
      gameModeState: e.gameModeState,
    });

    // Sync with uiBridge for non-React components (CTF scoreboard polling)
    uiBridge.setGameState(gameStateStore.getState());
  },
  "ui.showGameEnd": (e, world) => {
    world.deps.setGlobalState({
      gameEndData: {
        isWinner: e.isWinner,
        isDraw: e.isDraw,
        reason: e.reason,
        finalScores: e.finalScores,
        playerStats: e.playerStats,
        localTeamId: e.localTeamId,
        localPlayerId: e.localPlayerId,
      },
    });
  },
  "ui.joinButtonsReset": (e, world) => {
    if (world.deps.joinGameBtnEl) {
      world.deps.joinGameBtnEl.disabled = !e.enabled;
      world.deps.joinGameBtnEl.textContent = e.label;
    }
    if (world.deps.loggedInPlayBtnEl) {
      world.deps.loggedInPlayBtnEl.disabled = !e.enabled;
      world.deps.loggedInPlayBtnEl.textContent = e.label;
    }
  },
  "ui.damageNumber": (e, world) =>
    world.deps.damageNumberManager.createDamageNumber(
      e.x,
      e.y,
      e.damage,
      e.source,
    ),
  "ui.updateLobbyDisplay": (e, world) =>
    world.deps.updateLobbyDisplay(e.lobbyId),
  "metrics.smoothed": (e, world) =>
    world.deps.updateSmoothedMetrics({
      tps: e.tps,
      frameTime: e.frameTime,
      entities: e.entities,
      ping: e.ping,
    }),
  "screen.shake": (e, world) => world.deps.triggerScreenShake(e.intensity),
  "screen.flash": (e, world) =>
    world.deps.triggerScreenFlash(e.intensity, e.r, e.g, e.b),
  "webgl.bloomSpike": (e, world) => {
    if (world.deps.unifiedPostProcess) {
      world.deps.unifiedPostProcess.setConfig({
        bloom: {
          intensity: e.intensity || 2.5,
          threshold: e.threshold || 0.1,
        },
      });
      // Reset after delay to theme defaults
      setTimeout(() => {
        const theme = getTheme();
        world.deps.unifiedPostProcess.setConfig({
          bloom: {
            intensity: theme.postprocess.bloom.intensity,
            threshold: theme.postprocess.bloom.threshold,
          },
        });
      }, 200);
    }
  },
  "webgl.chromaticSpike": (e, world) => {
    if (world.deps.unifiedPostProcess) {
      world.deps.unifiedPostProcess.setConfig({
        chromaticAberration: { intensity: e.intensity || 8.0 },
      });
      setTimeout(() => {
        const theme = getTheme();
        world.deps.unifiedPostProcess.setConfig({
          chromaticAberration: {
            intensity: theme.postprocess.chromaticAberration.intensity,
          },
        });
      }, 150);
    }
  },
  "webgl.heatDistortion": (e, world) => {
    if (world.deps.unifiedPostProcess) {
      const worldWidth = world.deps.state.world.w;
      const worldHeight = world.deps.state.world.h;
      const normalizedX = e.x / worldWidth;
      const normalizedY = e.y / worldHeight;
      world.deps.unifiedPostProcess.heatDistort(
        normalizedX,
        normalizedY,
        e.intensity || 1.0,
        e.radius || 0.2,
      );
    }
  },
  "webgl.gpuExplosion": (e, world) => {
    if (world.deps.gpuExplosionParticles) {
      world.deps.gpuExplosionParticles.spawnExplosion(
        e.x,
        e.y,
        e.intensity || 1.0,
        e.colorPalette,
        e.radius || 80,
        e.preset,
      );
    }
  },
  "webgl.radialBlur": (e, world) => {
    if (world.deps.unifiedPostProcess) {
      const x = e.x !== undefined ? e.x : 0.5;
      const y = e.y !== undefined ? e.y : 0.5;
      world.deps.unifiedPostProcess.setConfig({
        radialBlur: {
          enabled: true,
          intensity: e.intensity || 0.3,
          centerX: x,
          centerY: y,
        },
      });
      setTimeout(() => {
        world.deps.unifiedPostProcess.setConfig({
          radialBlur: { enabled: false, intensity: 0 },
        });
      }, 200);
    }
  },
  "lobby.requestList": () => requestLobbyList(),
  "lobby.updateList": (e) => {
    const success = updateLobbyList(e.lobbies);
    if (!success) {
      showToast("Failed to update lobby list", "error");
    }
  },
  "react.updateState": (e) => {
    if ((window as any).updateReactState) {
      (window as any).updateReactState(e.state);
    } else {
      console.warn(
        "[REACT.UPDATESTATE] window.updateReactState not available yet",
      );
    }
  },
  "react.updateParty": (e: { party: any }) => {
    if ((window as any).updatePartyState) {
      (window as any).updatePartyState(e.party);
    } else {
      console.warn(
        "[REACT.UPDATEPARTY] window.updatePartyState not available yet",
      );
    }
  },
};

export function applyEffects(world: World) {
  for (const e of world.effects) {
    const handler = effectHandlers[e.kind];
    if (handler) {
      try {
        // @ts-expect-error - TypeScript can't infer that `e` matches the handler's specific effect type
        handler(e, world);
      } catch (error) {
        console.error(
          `[EFFECTS] Handler failed for effect kind: ${e.kind}`,
          error,
        );
        // Continue processing other effects even if one fails
      }
    } else {
      console.warn(
        `[EFFECTS] No handler registered for effect kind: ${e.kind}`,
      );
    }
  }
  world.effects.length = 0;
}
